import { FC } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { TedAward, TedNotice } from "../../../services/service-sales";
import BoxHolder from "../../../components/shared/BoxHolder/BoxHolder";
import {
  placeLine, noticeActions, PROCEDURE_TYPE_DEFAULTS, CONTRACT_NATURE_DEFAULTS,
  deadlineDayParts,
} from "./tedDisplay";
import styles from "./TedNoticeDetailPanel.module.scss";

type Props = { notice: TedNotice };

const ACTION_LABELS = {
  submission: "Lämna anbud",
  documents: "Förfrågningsunderlag",
  pdf: "Ladda ner",
  ted: "Visa på TED",
} as const;

/** Built from the day's own parts, so no timezone is applied to a calendar date. */
const formatDeadline = (notice: TedNotice) => {
  const parts = deadlineDayParts(notice);
  if (!parts) return "–";
  return moment({ year: parts.year, month: parts.month, day: parts.day })
    .locale("sv").format("D MMMM YYYY");
};

/* detail-sec-sub-title-title and detail-sec-sub-field are the app's label/value pair,
   shared with the supplier register's detail panel. */
const Fact: FC<{ label: string, children: React.ReactNode }> = ({ label, children }) => (
  <div className={styles.fact}>
    <span className="detail-sec-sub-title-title">{label}</span>
    <span className="detail-sec-sub-field">{children}</span>
  </div>
);

const SectionTitle: FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="detail-sec-title no-border">
    <span className="detail-sec-title-title">{children}</span>
  </div>
);

/**
 * Expanded row for a TED notice. Everything actionable points outward - the tender is run
 * by the buyer, and nothing here should suggest a bid is submitted through Accurator.
 *
 * sp-row-detail-wrapper and the detail-sec-* classes are the supplier register's, so the
 * two panels share one frame and one type scale instead of two copies of them. The
 * register's sp-row-detail-wrapper-wrapper is deliberately left out: its grey bleeds 10px
 * past each side, masked there by -pre/-post spacers this panel has no use for.
 */
export const TedNoticeDetailPanel: FC<Props> = ({ notice }) => {
  const [t] = useTranslation("requests");

  const winners = (award: TedAward) => {
    if (!award.awarded) {
      return <span className="detail-sec-sub-field">{t("published.ted.winner.notAwarded", "Ingen tilldelning")}</span>;
    }
    if (!award.winners.length) {
      return <span className="detail-sec-sub-field">{t("published.ted.winner.unnamed", "Vinnare ej angiven")}</span>;
    }
    return (
      <ul className={`detail-sec-sub-field ${styles.winners}`}>
        {award.winners.map((winner) => (
          <li key={winner.name}>
            {winner.name}
            {winner.organisationNumber && (
              <span className={styles.organisationNumber}> ({winner.organisationNumber})</span>
            )}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className={`sp-row-detail-wrapper ${styles.panel}`}>
      <div className="ar-row">
        <div className="ar-column detail-left-col">
          <BoxHolder className="box-holder-gap">
            <div className={styles.body}>
              <div className={styles.main}>
                {notice.description && (
                  <div className={styles.section}>
                    <SectionTitle>{t("published.ted.panel.description", "Beskrivning")}</SectionTitle>
                    <span className="detail-sec-sub-field">{notice.description}</span>
                  </div>
                )}

                {notice.award && (
                  <div className={styles.section}>
                    <SectionTitle>{t("published.ted.panel.winners", "Vinnare")}</SectionTitle>
                    {winners(notice.award)}
                  </div>
                )}

                <div className={styles.facts}>
                  <Fact label={t("published.ted.panel.buyer", "Beställare")}>
                    {notice.buyer?.name ?? "–"}
                    {notice.buyer?.city ? `, ${notice.buyer.city}` : ""}
                  </Fact>

                  {notice.buyer?.email && (
                    <Fact label={t("published.ted.panel.contact", "Kontakt")}>
                      <a href={`mailto:${notice.buyer.email}`}>{notice.buyer.email}</a>
                    </Fact>
                  )}

                  {notice.buyer?.url && (
                    <Fact label={t("published.ted.panel.buyerUrl", "Webbplats")}>
                      <a href={notice.buyer.url} target="_blank" rel="noopener noreferrer">
                        {notice.buyer.url.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                      </a>
                    </Fact>
                  )}

                  <Fact label={t("published.ted.panel.publicationNumber", "Annonsnummer")}>
                    {notice.publicationNumber}
                  </Fact>

                  {notice.cpv?.code && (
                    <Fact label={t("published.ted.panel.cpv", "CPV-kod")}>
                      {notice.cpv.label ? `${notice.cpv.code} – ${notice.cpv.label}` : notice.cpv.code}
                    </Fact>
                  )}

                  {notice.contractNature && (
                    <Fact label={t("published.ted.panel.contractNature", "Typ")}>
                      {t(
                        `published.ted.contractNature.${notice.contractNature}`,
                        CONTRACT_NATURE_DEFAULTS[notice.contractNature] ?? notice.contractNature,
                      )}
                    </Fact>
                  )}

                  {notice.procedureType && (
                    <Fact label={t("published.ted.panel.procedure", "Förfarande")}>
                      {t(
                        `published.ted.procedureType.${notice.procedureType}`,
                        PROCEDURE_TYPE_DEFAULTS[notice.procedureType] ?? notice.procedureType,
                      )}
                    </Fact>
                  )}

                  {!notice.award && (
                    <Fact label={t("published.ted.panel.deadline", "Sista anbudsdag")}>
                      {formatDeadline(notice)}
                      {notice.deadlineType === "request"
                        && ` (${t("published.ted.panel.requestDeadline", "sista dag att ansöka om att delta")})`}
                    </Fact>
                  )}

                  <Fact label={t("published.ted.panel.place", "Plats")}>
                    {placeLine(notice)}
                    {/* A few NUTS regions have no exact Accurator equivalent - Västra Götaland
                        covers Göteborg, Skaraborg and Älvsborg - so say when a place is widened. */}
                    {notice.placesAreApproximate
                      && ` (${t("published.ted.panel.approximatePlace", "ungefärligt område")})`}
                  </Fact>
                </div>
              </div>

              <div className={styles.actions}>
                {noticeActions(notice).map((action) => (
                  <a
                    key={action.key}
                    className={`bt-button ${action.primary ? "" : "white "}baseBtButtonSize ${styles.actionLink}`}
                    href={action.href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t(`published.ted.actions.${action.key}`, ACTION_LABELS[action.key])}
                  </a>
                ))}
              </div>
            </div>
          </BoxHolder>
        </div>
      </div>
    </div>
  );
};

export default TedNoticeDetailPanel;
